import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Elements, injectStripe, StripeProvider } from 'react-stripe-elements';
import Grid from '@material-ui/core/Grid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import translations from './translations';
import { AnimatePresence, motion } from 'framer-motion';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActionArea from '@material-ui/core/CardActionArea';
import ListItemText from '@material-ui/core/ListItemText';
import ListItem from '@material-ui/core/ListItem';
import Icon from '@afosto/app-components-react/Icon';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import IdealOption from './IdealOption/IdealOption';
import CreditCardOption from './CreditCardOption/CreditCardOption';
import styled from 'styled-components';

const DURATION = 0.1;
const FORM_TRANSITIONS = {
  initial: {
    opacity: 0,
    transform: 'translateY(5px)',
  },
  initialUp: {
    opacity: 0,
    transform: 'translateY(-5px)',
  },
  enter: {
    opacity: 1,
    transform: 'translateY(0px)',
    transition: {
      duration: DURATION,
      when: 'beforeChildren',
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transform: 'translateY(5px)',
    transition: {
      duration: DURATION,
      ease: 'easeOut',
    },
  },
  exitUp: {
    opacity: 0,
    transform: 'translateY(-5px)',
    transition: {
      duration: DURATION,
      ease: 'easeOut',
    },
  },
};

const OPTIONS_TRANSITIONS = {
  initial: {
    opacity: 0,
    transform: 'translateY(-5px)',
  },
  enter: {
    opacity: 1,
    transform: 'translateY(0px)',
    transition: {
      duration: DURATION,
      when: 'beforeChildren',
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transform: 'translateY(-5px)',
    transition: {
      duration: DURATION,
      ease: 'easeOut',
    },
  },
};

const PaymentMethodOption = styled(ListItem)`
  && {
    margin-top: 0;
    margin-bottom: 0;
  }
`;
const PaymentMethodOptionText = styled(ListItemText)`
  && {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const PaymentMethodForm = ({ onSubmit, stripe, innerRef }) => {
  const intl = useIntl();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  return (
    <AnimatePresence initial={false} exitBeforeEnter>
      {!selectedPaymentMethod && (
        <motion.div
          key="options"
          variants={OPTIONS_TRANSITIONS}
          initial="initial"
          animate="enter"
          exit="exit"
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Card>
                <CardActionArea onClick={() => setSelectedPaymentMethod('ideal')}>
                  <CardContent>
                    <PaymentMethodOption disableGutters dense>
                      <ListItemAvatar>
                        <Avatar>
                          <Icon icon={['fal', 'exchange']} />
                        </Avatar>
                      </ListItemAvatar>
                      <PaymentMethodOptionText
                        primary={intl.formatMessage(translations.options.iDeal.title)}
                        primaryTypographyProps={{ variant: 'subtitle1' }}
                        secondary={intl.formatMessage(translations.options.iDeal.description)}
                      />
                    </PaymentMethodOption>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardActionArea onClick={() => setSelectedPaymentMethod('creditcard')}>
                  <CardContent>
                    <PaymentMethodOption disableGutters dense>
                      <ListItemAvatar>
                        <Avatar>
                          <Icon icon={['fal', 'credit-card-front']} />
                        </Avatar>
                      </ListItemAvatar>
                      <PaymentMethodOptionText
                        primary={intl.formatMessage(translations.options.creditCard.title)}
                        primaryTypographyProps={{ variant: 'subtitle1' }}
                        secondary={intl.formatMessage(translations.options.creditCard.description)}
                      />
                    </PaymentMethodOption>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      )}

      {selectedPaymentMethod === 'creditcard' && (
        <motion.div
          key="creditcard"
          variants={FORM_TRANSITIONS}
          initial="initial"
          animate="enter"
          exit="exit"
        >
          <CreditCardOption
            stripe={stripe}
            innerRef={innerRef}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            onSubmit={onSubmit}
          />
        </motion.div>
      )}
      {selectedPaymentMethod === 'ideal' && (
        <motion.div
          key="ideal-start"
          variants={FORM_TRANSITIONS}
          initial="initial"
          animate="enter"
          exit="exit"
        >
          <IdealOption
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            stripe={stripe}
            innerRef={innerRef}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

PaymentMethodForm.propTypes = {
  onSubmit: PropTypes.func,
  stripe: PropTypes.object,
  innerRef: PropTypes.shape({ current: PropTypes.any }),
};

PaymentMethodForm.defaultProps = {
  onSubmit: undefined,
  stripe: undefined,
  innerRef: undefined,
};

const InjectedPaymentMethodForm = injectStripe(PaymentMethodForm);

const PaymentMethodFormContainer = ({ stripe, ...rest }) => (
  <StripeProvider stripe={stripe}>
    <Elements>
      <InjectedPaymentMethodForm {...rest} />
    </Elements>
  </StripeProvider>
);

PaymentMethodFormContainer.propTypes = {
  stripe: PropTypes.object,
};

PaymentMethodFormContainer.defaultProps = {
  stripe: undefined,
};

export default PaymentMethodFormContainer;
